import React, { Fragment, useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, { Field } from '@atlaskit/form';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Select from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import GradientRovoIcon from '../../common/assets/GradientRovoIcon';
import {
	type GenerateImageProps,
	IMAGE_GENERATION_THEMES,
	type ImageGenerationTheme,
} from '../../types';

const i18n = defineMessages({
	getCreativeLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-label',
		defaultMessage: 'Get creative with Rovo',
		description: 'Label text for the Rovo header generation entry point',
	},
	getCreativePlaceholder: {
		id: 'ai-image-generation.rovo-header-image-generator.get-creative-placeholder',
		defaultMessage: 'Describe an image to create',
		description: 'Placeholder text for the Rovo header generation entry point',
	},
	rovoIconAlt: {
		id: 'ai-image-generation.rovo-header-image-generator.rovo-icon-alt',
		defaultMessage: 'Rovo icon',
		description:
			'Alternative text for the Rovo gradient icon in the AI image generation interface.',
	},
	submitButtonLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.submit-button-label',
		defaultMessage: 'Generate AI header image',
		description: 'Label for the Rovo header generation submit button',
	},
	pickStyleLabel: {
		id: 'ai-image-generation.rovo-header-image-generator.pick-style-label',
		defaultMessage: 'Pick a style',
		description: 'Label for the style/theme picker dropdown',
	},
	stylePhotorealistic: {
		id: 'ai-image-generation.rovo-header-image-generator.style-photorealistic',
		defaultMessage: 'Photorealistic',
		description:
			'Style option in the image generation dropdown that creates realistic photograph-like images.',
	},
	styleAbstract: {
		id: 'ai-image-generation.rovo-header-image-generator.style-abstract',
		defaultMessage: 'Abstract',
		description:
			'Style option in the image generation dropdown that creates artistic images with geometric shapes and creative interpretations.',
	},
	styleWatercolors: {
		id: 'ai-image-generation.rovo-header-image-generator.style-watercolors',
		defaultMessage: 'Watercolors',
		description:
			'Style option in the image generation dropdown that creates watercolor painting-style images with soft, flowing colors.',
	},
	styleGraphicDesign: {
		id: 'ai-image-generation.rovo-header-image-generator.style-graphic-design',
		defaultMessage: 'Graphic Design',
		description:
			'Style option in the image generation dropdown that creates modern design images with clean layouts and bold typography.',
	},
	styleLandscape: {
		id: 'ai-image-generation.rovo-header-image-generator.style-landscape',
		defaultMessage: 'Landscape',
		description:
			'Style option in the image generation dropdown that creates landscape images featuring natural or urban scenery.',
	},
});

interface ImageGenerationFieldData {
	promptInput: string;
}

type RovoHeaderImageGeneratorProps = {
	deletePreviousHeaderImage?: () => void;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<void>;
	/**
	 * Optional extra action to render inline to the left of the submit button.
	 * The caller owns the element + its `onClick`; this component only renders it.
	 */
	moreActionsButton?: React.ReactNode;
	/**
	 * Optional text to prefill (and overwrite) the prompt text area.
	 * When provided (non-empty), this replaces any existing user input in the box.
	 */
	prefillText?: string;
};

const styles = cssMap({
	divider: {
		width: '100%',
		height: '1px',
		marginBlock: token('space.075'),
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	rovoIcon: {
		width: '16px',
		height: '16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	imagePromptContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		justifyContent: 'center',
		borderStyle: 'solid',
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface'),
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.100'),
		marginBlockStart: token('space.150'),
	},
	stack: {
		width: '100%',
		flexDirection: 'column',
	},
	actionsRow: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	actionsLeft: {
		display: 'flex',
		alignItems: 'center',
	},
});

export const RovoHeaderImageGenerator = ({
	generateAIHeaderImage,
	deletePreviousHeaderImage,
	moreActionsButton,
	prefillText,
}: RovoHeaderImageGeneratorProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [selectedTheme, setSelectedTheme] = useState<{
		label: string;
		value: ImageGenerationTheme;
	} | null>(null);
	const { trackAIInteractionInit, trackAIResultView, trackAIResultError } =
		useAIEventsInstrumentation({
			product: 'confluence',
			subproduct: 'confluence',
			aiFeatureName: 'aiHeaderImageGeneration',
			proactiveGeneratedAI: 0,
			userGeneratedAI: 1,
			enablePlatformEvents: true,
			source: 'rovoHeaderImageGenerator',
		});

	const themeOptions = [
		{
			label: formatMessage(i18n.stylePhotorealistic),
			value: IMAGE_GENERATION_THEMES.photorealistic,
		},
		{ label: formatMessage(i18n.styleAbstract), value: IMAGE_GENERATION_THEMES.abstract },
		{ label: formatMessage(i18n.styleWatercolors), value: IMAGE_GENERATION_THEMES.watercolors },
		{
			label: formatMessage(i18n.styleGraphicDesign),
			value: IMAGE_GENERATION_THEMES.graphic_design,
		},
		{ label: formatMessage(i18n.styleLandscape), value: IMAGE_GENERATION_THEMES.landscape },
	];

	const effectivePrefillText = prefillText?.trim() ? prefillText : undefined;

	// Atlaskit Form stores values by field name; `defaultValue` only applies on mount.
	// When `prefillText` changes, force overwrite the form state for `promptInput`.
	const setFieldValueRef = useRef<null | ((name: string, value: string) => void)>(null);
	const lastAppliedPrefillTextRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		// Do not update the form state if the image generation is not enabled
		if (!generateAIHeaderImage) {
			return;
		}

		if (!effectivePrefillText) {
			return;
		}

		if (lastAppliedPrefillTextRef.current === effectivePrefillText) {
			return;
		}

		lastAppliedPrefillTextRef.current = effectivePrefillText;
		setFieldValueRef.current?.('promptInput', effectivePrefillText);
	}, [effectivePrefillText, generateAIHeaderImage]);

	if (!generateAIHeaderImage) {
		return null;
	}

	return (
		<>
			<Box xcss={styles.divider} />

			{fg('platform-design_system_team-form_conversion') ? (
				<Form
					onSubmit={async (data: ImageGenerationFieldData) => {
						const useRawPrompt = !selectedTheme?.value;

						// Track that user initiated AI image generation
						trackAIInteractionInit({
							attributes: {
								promptLength: data.promptInput?.length || 0,
								useRawPrompt,
								selectedTheme: selectedTheme?.value || 'none',
							},
						});

						deletePreviousHeaderImage?.();

						try {
							// Generate the AI header image with acceptance flow for repositioning UI
							await generateAIHeaderImage({
								adfContent: data.promptInput,
								useRawPrompt,
								showAcceptanceFlow: true,
								...(selectedTheme?.value && { theme: selectedTheme.value }),
							});

							// Track successful result view
							trackAIResultView({
								attributes: {
									promptLength: data.promptInput?.length || 0,
									useRawPrompt,
									selectedTheme: selectedTheme?.value || 'none',
								},
							});
						} catch (error) {
							// Track error if generation fails
							trackAIResultError({
								aiErrorMessage: error instanceof Error ? error.message : 'Unknown error',
								aiErrorCode: (error as any)?.code,
							});
						}
					}}
				>
					{({ formProps, setFieldValue }: any) => {
						setFieldValueRef.current = setFieldValue;
						return (
							<form {...formProps}>
								<Inline space="space.075" alignBlock="center">
									<Box xcss={styles.rovoIcon}>
										<GradientRovoIcon label={formatMessage(i18n.rovoIconAlt)} />
									</Box>
									<label htmlFor="get-creative-text-area">
										<Text size="medium" id="get-creative-label">
											{formatMessage(i18n.getCreativeLabel)}
										</Text>
									</label>
								</Inline>

								<Stack space="space.100">
									<Box>
										<label htmlFor="theme-picker">
											<Text size="small" weight="semibold">
												{formatMessage(i18n.pickStyleLabel)}
											</Text>
										</label>
										<Box paddingBlockStart="space.050">
											<Select
												inputId="theme-picker"
												options={themeOptions}
												value={selectedTheme}
												onChange={(newValue) => setSelectedTheme(newValue)}
												placeholder={formatMessage(i18n.pickStyleLabel)}
											/>
										</Box>
									</Box>
								</Stack>

								<Box xcss={styles.imagePromptContainer}>
									{/* Not using a Stack component directly as it prevents the TextArea from expanding */}
									<Box xcss={styles.stack}>
										<Field
											// Remount when the prefill text changes to ensure it overwrites any existing user input.
											key={effectivePrefillText ?? 'promptInput'}
											isRequired
											name="promptInput"
											id="get-creative-text-area"
											defaultValue={effectivePrefillText ?? ''}
										>
											{({ fieldProps }: any) => (
												<Fragment>
													<TextArea
														placeholder={formatMessage(i18n.getCreativePlaceholder)}
														appearance="none"
														{...fieldProps}
													/>
												</Fragment>
											)}
										</Field>
										<Box xcss={styles.actionsRow}>
											<Box xcss={styles.actionsLeft}>{moreActionsButton}</Box>
											<IconButton
												appearance="primary"
												icon={ArrowUpIcon}
												label={formatMessage(i18n.submitButtonLabel)}
												shape="circle"
												type="submit"
											/>
										</Box>
									</Box>
								</Box>
							</form>
						);
					}}
				</Form>
			) : (
				<Form
					onSubmit={async (data: ImageGenerationFieldData) => {
						const useRawPrompt = !selectedTheme?.value;

						// Track that user initiated AI image generation
						trackAIInteractionInit({
							attributes: {
								promptLength: data.promptInput?.length || 0,
								useRawPrompt,
								selectedTheme: selectedTheme?.value || 'none',
							},
						});

						deletePreviousHeaderImage?.();

						try {
							// Generate the AI header image with acceptance flow for repositioning UI
							await generateAIHeaderImage({
								adfContent: data.promptInput,
								useRawPrompt,
								showAcceptanceFlow: true,
								...(selectedTheme?.value && { theme: selectedTheme.value }),
							});

							// Track successful result view
							trackAIResultView({
								attributes: {
									promptLength: data.promptInput?.length || 0,
									useRawPrompt,
									selectedTheme: selectedTheme?.value || 'none',
								},
							});
						} catch (error) {
							// Track error if generation fails
							trackAIResultError({
								aiErrorMessage: error instanceof Error ? error.message : 'Unknown error',
								aiErrorCode: (error as any)?.code,
							});
						}
					}}
				>
					{({ formProps, setFieldValue }: any) => {
						setFieldValueRef.current = setFieldValue;
						return (
							<form {...formProps}>
								<Inline space="space.075" alignBlock="center">
									<Box xcss={styles.rovoIcon}>
										<GradientRovoIcon label={formatMessage(i18n.rovoIconAlt)} />
									</Box>
									<label htmlFor="get-creative-text-area">
										<Text size="medium" id="get-creative-label">
											{formatMessage(i18n.getCreativeLabel)}
										</Text>
									</label>
								</Inline>

								<Stack space="space.100">
									<Box>
										<label htmlFor="theme-picker">
											<Text size="small" weight="semibold">
												{formatMessage(i18n.pickStyleLabel)}
											</Text>
										</label>
										<Box paddingBlockStart="space.050">
											<Select
												inputId="theme-picker"
												options={themeOptions}
												value={selectedTheme}
												onChange={(newValue) => setSelectedTheme(newValue)}
												placeholder={formatMessage(i18n.pickStyleLabel)}
											/>
										</Box>
									</Box>
								</Stack>

								<Box xcss={styles.imagePromptContainer}>
									{/* Not using a Stack component directly as it prevents the TextArea from expanding */}
									<Box xcss={styles.stack}>
										<Field
											// Remount when the prefill text changes to ensure it overwrites any existing user input.
											key={effectivePrefillText ?? 'promptInput'}
											isRequired
											name="promptInput"
											id="get-creative-text-area"
											defaultValue={effectivePrefillText ?? ''}
										>
											{({ fieldProps }: any) => (
												<Fragment>
													<TextArea
														placeholder={formatMessage(i18n.getCreativePlaceholder)}
														appearance="none"
														{...fieldProps}
													/>
												</Fragment>
											)}
										</Field>
										<Box xcss={styles.actionsRow}>
											<Box xcss={styles.actionsLeft}>{moreActionsButton}</Box>
											<IconButton
												appearance="primary"
												icon={ArrowUpIcon}
												label={formatMessage(i18n.submitButtonLabel)}
												shape="circle"
												type="submit"
											/>
										</Box>
									</Box>
								</Box>
							</form>
						);
					}}
				</Form>
			)}
		</>
	);
};
